import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import { legacyOperationGate } from './legacyOperationGate'
import {
  selectCheckedSubIds,
  selectEditorState,
  selectMainState,
  selectSubRenderState,
} from './selectors'
import { treeStore } from './MsgTreeStore'
import { currentLegacyPagingMode } from './projectionSync'
import type {
  LegacyBusyTransitionKind,
  LegacyLifecycleSnapshot,
  LegacyLifecycleStateListener,
  LegacySectionInput,
  MsgObjCommandTarget,
  MsgObjEditorState,
  MsgObjMainState,
  MsgObjSemanticCommand,
  MsgObjState,
  MsgObjTemplateState,
  SubRenderState,
  SubsRenderProjection,
  SubsSection,
  SubsStoreState,
} from './types'

class MsgObjPipeline {
  constructor(private readonly subsStore: typeof treeStore) {}

  public get subs() {
    return {
      getState: (): SubsStoreState => this.subsStore.getState(),
      getRenderState: (section?: SubsSection): SubsRenderProjection | number[] => {
        const projection = this.subsStore.getState().renderProjection;
        return section ? projection.rootIds[section] : projection;
      },
      getSubRenderState: (elId: number): SubRenderState | null =>
        selectSubRenderState(this.subsStore.getState(), elId),
      getCheckedIds: (section: LegacySectionInput = 'answers', mode = currentLegacyPagingMode()): number[] =>
        selectCheckedSubIds(this.subsStore.getState(), section, mode),
      parseAndNormalize: (html: string, options?: any) => this.subsStore.parseAndNormalize(html, options),
      removeAnswerSubsSelector: (selector: string) => this.subsStore.removeAnswerSubsSelector(selector),
      mutate: this.subsStore.mutate,
    };
  }

  public get main() {
    return {
      getState: (): MsgObjMainState =>
        selectMainState(this.subsStore.getState()),
    };
  }

  public get editor() {
    return {
      getState: (): MsgObjEditorState =>
        selectEditorState(this.subsStore.getState()),
    };
  }

  public get template() {
    return {
      getState: (): MsgObjTemplateState => ({
        data: this.subsStore.getState().templateData,
      }),
      updateSlot: (slot: string, type: 'text' | 'val' | 'html', value: string): void =>
        this.subsStore.mutate.updateTemplateData(slot, type, value),
    };
  }

  public get legacy() {
    return {
      run: <T>(reason: string, fn: () => T): Promise<Awaited<T>> =>
        legacyOperationGate.run(reason, fn),
      whenIdle: (reason?: string): Promise<void> =>
        legacyOperationGate.whenIdle(reason),
      getState: (): LegacyLifecycleSnapshot =>
        legacyOperationGate.snapshot(),
      subscribe: (listener: LegacyLifecycleStateListener): (() => void) =>
        legacyOperationGate.subscribe(listener),
      install: (): boolean =>
        legacyOperationGate.install(),
      noteBusyTransition: (kind: LegacyBusyTransitionKind, reason?: string, details?: unknown): void =>
        legacyOperationGate.noteBusyTransition(kind, reason, details),
    };
  }

  public get command() {
    return {
      normalizeLegacyRjqOp: this.subsStore.command.normalizeLegacyRjqOp,
      acceptLegacyRjqOp: this.subsStore.command.acceptLegacyRjqOp,
      resolveLegacyUiCommand: this.subsStore.command.resolveLegacyUiCommand,
      applyLegacyUiOp: this.subsStore.command.applyLegacyUiOp,
      send: this.subsStore.command.send,
      setActive: this.subsStore.command.setActive,
      setChecked: this.subsStore.command.setChecked,
      checkSub: this.subsStore.command.checkSub,
      checkVisibleSubs: this.subsStore.command.checkVisibleSubs,
      moveCheckedTo: this.subsStore.command.moveCheckedTo,
      moveCheckedToTrash: this.subsStore.command.moveCheckedToTrash,
      moveCheckedToDrafts: this.subsStore.command.moveCheckedToDrafts,
      moveToTrash: this.subsStore.command.moveToTrash,
      moveToDrafts: this.subsStore.command.moveToDrafts,
      restoreChecked: this.subsStore.command.restoreChecked,
      setVisible: this.subsStore.command.setVisible,
      setCurrentPage: this.subsStore.command.setCurrentPage,
      showArray: this.subsStore.command.showArray,
      delete: this.subsStore.command.delete,
      legacyEvent: this.subsStore.command.legacyEvent,
    };
  }

  /**
   * Compatibility escape hatch. New code should prefer msgObj.subs/main/editor/template.
   */
  public get mutate() {
    return this.subsStore.mutate;
  }

  public subscribe = (listener: () => void) => this.subsStore.subscribe(listener);

  public getState = (): MsgObjState => {
    const subs = this.subsStore.getState();
    return {
      syncVersion: subs.syncVersion,
      subs,
      main: this.main.getState(),
      editor: this.editor.getState(),
      template: this.template.getState(),
    };
  };
}

// msgObj is the canonical page data facade. treeStore remains the subs/tree
// projection used by existing components and compatibility hooks.
export const msgObj = new MsgObjPipeline(treeStore);
getMsgDeskRuntime().setGlobal('msgObj', msgObj);

export type {
  MsgObjCommandTarget,
  MsgObjEditorState,
  MsgObjMainState,
  MsgObjSemanticCommand,
  MsgObjState,
  MsgObjTemplateState,
  SubsSection,
} from './types'
